import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  GitBranch,
  Plus,
  Film,
  Unlink,
  Layers,
  Image,
  FileVideo
} from 'lucide-react'
import { toast } from 'sonner'
import type { Clip, VfxShot, VfxShotClip, VfxShotClipRole } from '@/types/database'
import {
  getVfxShotClips,
  unlinkClipFromVfxShot
} from '@/lib/api/vfxShotClips'
import { LinkClipDialog } from './LinkClipDialog'

interface VfxShotRelationshipsProps {
  vfxShot: VfxShot
  availableClips: Clip[]
  onUpdate?: () => void
  canLink?: boolean
}

// Helper function to get icon for relationship type
const getRelationshipIcon = (type: VfxShotClipRole) => {
  switch (type) {
    case 'plate':
      return <Image className="h-4 w-4" />
    case 'comp':
    case 'final':
      return <Layers className="h-4 w-4" />
    case 'matte':
      return <FileVideo className="h-4 w-4" />
    case 'source':
    case 'reference':
    case 'render':
    default:
      return <Film className="h-4 w-4" />
  }
}

// Helper function to get badge color for relationship type
const getRelationshipBadgeVariant = (type: VfxShotClipRole): "default" | "secondary" | "destructive" | "outline" => {
  switch (type) {
    case 'source':
      return 'default'
    case 'plate':
      return 'secondary'
    case 'comp':
    case 'final':
      return 'default'
    case 'matte':
    case 'clean_plate':
      return 'outline'
    default:
      return 'secondary'
  }
}

// Helper function to format relationship type for display
const formatRelationType = (type: VfxShotClipRole): string => {
  switch (type) {
    case 'clean_plate':
      return 'Clean Plate'
    default:
      return type.charAt(0).toUpperCase() + type.slice(1)
  }
}

export function VfxShotRelationships({
  vfxShot,
  availableClips,
  onUpdate,
  canLink = true
}: VfxShotRelationshipsProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [clipLinks, setClipLinks] = useState<VfxShotClip[]>([])
  const [loading, setLoading] = useState(true)

  // Load clip links
  useEffect(() => {
    loadClipLinks()
  }, [vfxShot.id])

  const loadClipLinks = async () => {
    try {
      setLoading(true)
      const links = await getVfxShotClips(vfxShot.id)
      setClipLinks(links)
    } catch (error: any) {
      console.error('Failed to load clip links:', error)
      toast.error('Failed to load linked clips')
    } finally {
      setLoading(false)
    }
  }

  const handleUnlinkClip = async (linkId: string, clipName: string, relationType: VfxShotClipRole) => {
    try {
      await unlinkClipFromVfxShot(vfxShot.id, linkId, relationType)
      toast.success(`${formatRelationType(relationType)} clip "${clipName}" unlinked`)
      await loadClipLinks()
      onUpdate?.()
    } catch (error: any) {
      console.error('Failed to unlink clip:', error)
      toast.error(error.message || 'Failed to unlink clip')
    }
  }

  const handleLinkSuccess = async () => {
    await loadClipLinks()
    onUpdate?.()
    setAddDialogOpen(false)
  }

  // Get clip details for each link (would need to be populated from API or passed in)
  const getClipForLink = (link: VfxShotClip): Clip | undefined => {
    return availableClips.find(c => c.id === link.clip_id)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Relationships</CardTitle>
            {clipLinks.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {clipLinks.length}
              </Badge>
            )}
          </div>
          {canLink && (
            <Button
              size="sm"
              onClick={() => setAddDialogOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Link Clip
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">Loading clips...</p>
          </div>
        ) : clipLinks.length === 0 ? (
          <div className="text-center py-8">
            <GitBranch className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              No clips linked yet. Link source clips, plates, or comps to this VFX shot.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {clipLinks.map((link) => {
              const clip = getClipForLink(link)
              if (!clip) return null

              return (
                <Card key={link.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">
                        {getRelationshipIcon(link.relationship_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h4 className="font-semibold text-sm">{clip.name}</h4>
                          <Badge
                            variant={getRelationshipBadgeVariant(link.relationship_type)}
                            className="text-xs"
                          >
                            {formatRelationType(link.relationship_type)}
                          </Badge>
                          {link.track_number && (
                            <Badge variant="outline" className="text-xs">
                              Track {link.track_number}
                              {link.track_name && `: ${link.track_name}`}
                            </Badge>
                          )}
                        </div>

                        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                          {/* Frame range info */}
                          {link.clip_frame_start !== null && link.clip_frame_start !== undefined &&
                           link.clip_frame_end !== null && link.clip_frame_end !== undefined && (
                            <span>
                              Frames: {link.clip_frame_start} - {link.clip_frame_end}
                              {' '}({link.clip_frame_end - link.clip_frame_start + 1} frames)
                            </span>
                          )}

                          {/* Clip metadata */}
                          {clip.tape_name && (
                            <span>Tape: {clip.tape_name}</span>
                          )}
                          {clip.source_timecode && (
                            <span>TC: {clip.source_timecode}</span>
                          )}

                          {/* Notes */}
                          {link.notes && (
                            <span className="italic mt-1">{link.notes}</span>
                          )}

                          {/* Clip tags */}
                          {clip.tags && clip.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {clip.tags.slice(0, 3).map((tag, i) => (
                                <Badge key={i} variant="secondary" className="text-xs px-1 py-0">
                                  {tag}
                                </Badge>
                              ))}
                              {clip.tags.length > 3 && (
                                <Badge variant="secondary" className="text-xs px-1 py-0">
                                  +{clip.tags.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {canLink && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlinkClip(link.clip_id, clip.name, link.relationship_type)}
                        className="text-muted-foreground hover:text-destructive"
                        title="Unlink clip"
                      >
                        <Unlink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {/* Link Clip Dialog */}
        <LinkClipDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          vfxShot={vfxShot}
          availableClips={availableClips}
          existingLinks={clipLinks}
          onSuccess={handleLinkSuccess}
        />
      </CardContent>
    </Card>
  )
}
